<script lang="ts">
  import { Activity, Wrench } from 'lucide-svelte';
  import type { LiveMonitorAggregate } from '$lib/api/liveMonitor';
  import { Card } from '$lib/components/ui/card';
  import { liveLookStore } from '$lib/stores/liveLook.svelte';
  import { type UnrealizedPnLReason, livePriceStore } from '$lib/stores/livePrice.svelte';

  interface Props {
    aggregate: LiveMonitorAggregate;
    liveUnrealizedTotal: number | null;
    // Motivo cuando liveUnrealizedTotal es null y hay open trades, para que
    // la celda Live P&L muestre una etiqueta accionable en lugar de "N/A".
    liveUnrealizedReason?: UnrealizedPnLReason;
    accountBreakdown?: Array<{ account: string; count: number; symbols: string[] }>;
    mode: 'live' | 'developer';
  }

  let {
    aggregate,
    liveUnrealizedTotal,
    liveUnrealizedReason = null,
    accountBreakdown = [],
    mode,
  }: Props = $props();

  // Etiqueta cuando no se puede computar el numero. Igual logica que en
  // SessionCard: prioriza no-vpp (problema sistemico) sobre waiting-price.
  let upstreamDown = $derived(
    livePriceStore.status !== 'open' || livePriceStore.upstreamStatus !== 'open',
  );
  let pnlLabel = $derived.by((): string => {
    if (liveUnrealizedReason === 'no-vpp') return 'No vpp';
    if (liveUnrealizedReason === 'no-trade') return 'No data';
    if (liveUnrealizedReason === 'waiting-price') {
      return upstreamDown ? 'Connecting…' : 'Waiting tick';
    }
    return 'N/A';
  });
  let pnlLabelTitle = $derived.by((): string => {
    if (liveUnrealizedReason === 'no-vpp') {
      return 'One or more open trades missing valuePerPoint - cannot compute';
    }
    if (liveUnrealizedReason === 'no-trade') return 'Missing trade fields (side/entry/qty)';
    if (liveUnrealizedReason === 'waiting-price') {
      return upstreamDown
        ? 'Price feed connecting - try again in a few seconds'
        : 'Connected - waiting for next price tick';
    }
    return '';
  });

  // useDevLook: apariencia cyan. Siempre true para developer mode;
  // en live depende del toggle "Live looks like Dev" (preferencia user).
  // El TÍTULO usa `mode` (sigue diciendo "Aggregate Performance" en live),
  // los COLORES usan `useDevLook`.

  function fmtPnL(n: number | null): string {
    if (n == null) return 'N/A';
    if (n === 0) return '$0.00';
    const sign = n > 0 ? '+' : '-';
    return `${sign}$${Math.abs(n).toFixed(2)}`;
  }

  function fmtPF(pf: number | null): string {
    if (pf === null) return '∞';
    if (!Number.isFinite(pf)) return '—';
    return pf.toFixed(2);
  }

  let isDev = $derived(mode === 'developer');
  let useDevLook = $derived(isDev || liveLookStore.liveLooksLikeDev);
  // Title/Icon: cyan/teal cuando useDevLook (Developer mode o toggle "Live
  // looks like Dev"); gold por default. Distintivo vs --primary (naranja
  // Rabbit o verde Classic), buena legibilidad sobre dark.
  let titleColorClass = $derived(useDevLook ? 'text-cyan-300' : 'text-gold');
  let iconColorClass = $derived(useDevLook ? 'text-cyan-300' : 'text-gold');
  let borderColorClass = $derived(useDevLook ? 'border-cyan-500/30' : 'border-gold/30');
  let gradientClass = $derived(
    useDevLook ? 'from-cyan-500/5 to-transparent' : 'from-gold/5 to-transparent',
  );
  let title = $derived(isDev ? 'Developer Performance' : 'Aggregate Performance');
</script>

<Card
  class={[
    'sticky top-14 z-30 bg-card-hero lifted-edge-gold shadow-luxury-gold',
    borderColorClass,
  ].join(' ')}
>
  <div class="flex flex-wrap items-center gap-3 px-6 py-3">
    <!-- Icon + title (count integrado como subtitle, no span suelto que
         desalineaba contra el h2 con font-serif). -->
    <div class="flex items-center gap-2">
      {#if isDev}
        <Wrench class={['size-5', iconColorClass].join(' ')} />
      {:else}
        <Activity class={['size-5', iconColorClass].join(' ')} />
      {/if}
      <h2 class={['text-display text-2xl', titleColorClass].join(' ')}>
        {title}
      </h2>
      <span class="text-muted-foreground text-xs tabular-nums">
        · {aggregate.activeSessionsCount}
        {aggregate.activeSessionsCount === 1 ? 'session' : 'sessions'}
      </span>
    </div>

    <!-- Account breakdown chips -->
    {#if accountBreakdown.length > 0}
      <div class="ml-auto flex flex-wrap items-center gap-1.5">
        {#each accountBreakdown as a (a.account)}
          <span
            class={[
              'rounded-md border px-2 py-0.5 text-[11px] tabular-nums',
              useDevLook
                ? 'border-cyan-500/40 bg-cyan-500/10 text-cyan-300'
                : 'border-gold/40 bg-gold/10 text-gold',
            ].join(' ')}
          >
            <span class="font-semibold">{a.account}</span>: {a.count} ({a.symbols.join(', ')})
          </span>
        {/each}
      </div>
    {/if}
  </div>

  <div class="grid grid-cols-2 gap-x-6 gap-y-3 border-t px-6 py-4 sm:grid-cols-3 md:grid-cols-6">
    <div>
      <div class="text-eyebrow">Total P&L</div>
      <div
        class={[
          'font-mono text-xl font-semibold tabular-nums',
          aggregate.totalPnL > 0 && 'text-success',
          aggregate.totalPnL < 0 && 'text-destructive',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {fmtPnL(aggregate.totalPnL)}
      </div>
      {#if aggregate.totalTrades > 0}
        <div
          class="text-muted-foreground/70 font-mono text-[11px] tabular-nums"
          title="BT P&L (sin slippage, trigger prices)"
        >
          BT {fmtPnL(aggregate.totalBtPnL)}
        </div>
      {/if}
    </div>

    <div>
      <div class="text-eyebrow">Win Rate</div>
      <div class="font-mono text-xl font-semibold tabular-nums">
        {aggregate.totalTrades > 0 ? `${aggregate.winRate.toFixed(1)}%` : '—'}
      </div>
    </div>

    <div>
      <div class="text-eyebrow">Total Trades</div>
      <div class="font-mono text-xl font-semibold tabular-nums">{aggregate.totalTrades}</div>
    </div>

    <div>
      <div class="text-eyebrow">Open Positions</div>
      <div
        class={[
          'font-mono text-xl font-semibold tabular-nums',
          aggregate.totalOpenPositions > 0 && (useDevLook ? 'text-cyan-300' : 'text-gold'),
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {aggregate.totalOpenPositions}
      </div>
    </div>

    <div>
      <div class="text-eyebrow">Profit Factor</div>
      <div class="font-mono text-xl font-semibold tabular-nums">{fmtPF(aggregate.profitFactor)}</div>
    </div>

    <div>
      <div class="text-eyebrow">
        Live P&L <span class="text-warning">⚡</span>
      </div>
      <div
        class={[
          'font-mono text-xl font-semibold tabular-nums',
          liveUnrealizedTotal != null && liveUnrealizedTotal > 0 && 'text-success',
          liveUnrealizedTotal != null && liveUnrealizedTotal < 0 && 'text-destructive',
          // Etiqueta de estado (no-vpp en warning, resto atenuado).
          liveUnrealizedTotal == null && liveUnrealizedReason === 'no-vpp' && 'text-warning',
          liveUnrealizedTotal == null && liveUnrealizedReason !== 'no-vpp' && 'text-muted-foreground',
        ]
          .filter(Boolean)
          .join(' ')}
        title={pnlLabelTitle}
      >
        {#if liveUnrealizedTotal != null}
          {fmtPnL(liveUnrealizedTotal)}
        {:else}
          <span class="text-sm font-normal">{pnlLabel}</span>
        {/if}
      </div>
    </div>
  </div>
</Card>
